import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Sprout,
  TrendingUp,
  MapPin,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Zap,
  Globe,
  Truck,
  Building2,
  Leaf,
  Sun,
  Droplets
} from 'lucide-react';

const ExecutiveDashboard = ({ userRole = 'admin' }) => {
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalProducers: 0,
      totalCrops: 0,
      totalArea: 0,
      activeProjects: 0,
      monthlyGrowth: 0,
      productivityIndex: 0
    },
    kpis: {
      producerSatisfaction: 0,
      cropYield: 0,
      operationalEfficiency: 0,
      costReduction: 0,
      timeToMarket: 0,
      qualityScore: 0
    },
    trends: {
      producersGrowth: [],
      cropsGrowth: [],
      revenueGrowth: [],
      productivityTrend: []
    },
    alerts: [],
    recentActivities: [],
    weatherData: {
      temperature: 0,
      humidity: 0,
      rainfall: 0,
      forecast: []
    }
  });

  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedWidgets, setSelectedWidgets] = useState({
    overview: true,
    kpis: true,
    trends: true,
    alerts: true,
    activities: true,
    weather: true
  });

  useEffect(() => {
    loadDashboardData();
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos de diferentes APIs
      const [producersRes, cropsRes, statsRes, activitiesRes] = await Promise.all([
        fetch('/api/producers'),
        fetch('/api/crops'),
        fetch('/api/dashboard/stats'),
        fetch('/api/dashboard/activities')
      ]);

      const [producersData, cropsData, statsData, activitiesData] = await Promise.all([
        producersRes.json(),
        cropsRes.json(),
        statsRes.json(),
        activitiesRes.json()
      ]);

      const cropsList = Array.isArray(cropsData) ? cropsData : (cropsData.data || []);
      const activitiesList = Array.isArray(activitiesData) ? activitiesData : (activitiesData.data || []);

      // Procesar datos para el dashboard
      const processedData = {
        overview: {
          totalProducers: producersData.data?.length || producersData.length || 0,
          totalCrops: cropsList.length,
          totalArea: parseFloat(cropsList.reduce((sum, crop) => sum + (parseFloat(crop.area) || 0), 0).toFixed(1)),
          activeProjects: statsData.active_projects || 12,
          monthlyGrowth: statsData.monthly_growth || 8.5,
          productivityIndex: statsData.productivity_index || 85.2
        },
        kpis: {
          producerSatisfaction: statsData.producer_satisfaction || 92,
          cropYield: statsData.crop_yield || 78.5,
          operationalEfficiency: statsData.operational_efficiency || 88.3,
          costReduction: statsData.cost_reduction || 15.7,
          timeToMarket: statsData.time_to_market || 45,
          qualityScore: statsData.quality_score || 94.1
        },
        trends: {
          producersGrowth: statsData.producers_growth || generateMockTrendData(),
          cropsGrowth: statsData.crops_growth || generateMockTrendData(),
          revenueGrowth: statsData.revenue_growth || generateMockTrendData(),
          productivityTrend: statsData.productivity_trend || generateMockTrendData()
        },
        alerts: Array.isArray(statsData.alerts) ? statsData.alerts : generateMockAlerts(),
        recentActivities: activitiesList.map((act, idx) => ({
          id: act.id || idx,
          action: act.title || act.action || "Actividad registrada",
          user: act.description?.split(" se ha")[0]?.split(" ha sido")[0] || act.user || "Sistema",
          time: act.created_at ? "hace poco" : (act.time || "2h ago")
        })),
        weatherData: {
          temperature: 28.5,
          humidity: 65,
          rainfall: 12.3,
          forecast: [
            { day: 'Hoy', temp: 28, condition: 'Soleado' },
            { day: 'Mañana', temp: 26, condition: 'Parcialmente nublado' },
            { day: 'Pasado', temp: 24, condition: 'Lluvia ligera' }
          ]
        }
      };

      setDashboardData(processedData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Usar datos mock en caso de error
      setDashboardData(generateMockDashboardData());
    } finally {
      setLoading(false);
    }
  };

  const generateMockTrendData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2024, i).toLocaleDateString('es', { month: 'short' }),
      value: Math.floor(Math.random() * 100) + 50
    }));
  };

  const generateMockAlerts = () => [
    { id: 1, type: 'warning', message: '3 cultivos requieren atención inmediata', time: '2h ago' },
    { id: 2, type: 'info', message: 'Nuevo productor registrado en Comuna 2', time: '4h ago' },
    { id: 3, type: 'success', message: 'Cosecha de maíz completada exitosamente', time: '6h ago' }
  ];

  const generateMockActivities = () => [
    { id: 1, type: 'producer', action: 'Nuevo productor registrado', user: 'Juan Pérez', time: '2h ago' },
    { id: 2, type: 'crop', action: 'Cultivo actualizado', user: 'María García', time: '4h ago' },
    { id: 3, type: 'supply', action: 'Solicitud de insumos aprobada', user: 'Carlos López', time: '6h ago' }
  ];

  const generateMockDashboardData = () => ({
    overview: {
      totalProducers: 156,
      totalCrops: 89,
      totalArea: 1250.5,
      activeProjects: 12,
      monthlyGrowth: 8.5,
      productivityIndex: 85.2
    },
    kpis: {
      producerSatisfaction: 92,
      cropYield: 78.5,
      operationalEfficiency: 88.3,
      costReduction: 15.7,
      timeToMarket: 45,
      qualityScore: 94.1
    },
    trends: {
      producersGrowth: generateMockTrendData(),
      cropsGrowth: generateMockTrendData(),
      revenueGrowth: generateMockTrendData(),
      productivityTrend: generateMockTrendData()
    },
    alerts: generateMockAlerts(),
    recentActivities: generateMockActivities(),
    weatherData: {
      temperature: 28.5,
      humidity: 65,
      rainfall: 12.3,
      forecast: [
        { day: 'Hoy', temp: 28, condition: 'Soleado' },
        { day: 'Mañana', temp: 26, condition: 'Parcialmente nublado' },
        { day: 'Pasado', temp: 24, condition: 'Lluvia ligera' }
      ]
    }
  });

  const getKpiColor = (value, type) => {
    if (type === 'percentage') {
      if (value >= 90) return 'text-green-600';
      if (value >= 70) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (type === 'time') {
      if (value <= 30) return 'text-green-600';
      if (value <= 60) return 'text-yellow-600';
      return 'text-red-600';
    }
    return 'text-blue-600';
  };

  const getKpiIcon = (key) => {
    const icons = {
      producerSatisfaction: Users,
      cropYield: Sprout,
      operationalEfficiency: Zap,
      costReduction: DollarSign,
      timeToMarket: Clock,
      qualityScore: Target
    };
    return icons[key] || Activity;
  };

  const toggleWidget = (widget) => {
    setSelectedWidgets(prev => ({
      ...prev,
      [widget]: !prev[widget]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Ejecutivo</h1>
          <p className="text-gray-600">Vista integral del sistema SISCIAC</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
            <option value="1y">Último año</option>
          </select>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Actualizar
          </button>
        </div>
      </div>

      {/* Widget Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold mb-3">Widgets Disponibles</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(selectedWidgets).map(([widget, enabled]) => (
            <label key={widget} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={() => toggleWidget(widget)}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm capitalize">{widget}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      {selectedWidgets.overview && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Productores</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.overview.totalProducers}</p>
              </div>
              <Users className="h-12 w-12 text-blue-600" />
            </div>
            <div className="mt-4 flex items-center">
              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
              <span className="text-sm text-green-600">+{dashboardData.overview.monthlyGrowth}% este mes</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cultivos</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.overview.totalCrops}</p>
              </div>
              <Sprout className="h-12 w-12 text-green-600" />
            </div>
            <div className="mt-4 flex items-center">
              <MapPin className="h-4 w-4 text-blue-600 mr-1" />
              <span className="text-sm text-blue-600">{dashboardData.overview.totalArea} hectáreas</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Proyectos Activos</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.overview.activeProjects}</p>
              </div>
              <Target className="h-12 w-12 text-purple-600" />
            </div>
            <div className="mt-4 flex items-center">
              <Activity className="h-4 w-4 text-green-600 mr-1" />
              <span className="text-sm text-green-600">En progreso</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Productividad</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.overview.productivityIndex}%</p>
              </div>
              <BarChart3 className="h-12 w-12 text-orange-600" />
            </div>
            <div className="mt-4 flex items-center">
              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
              <span className="text-sm text-green-600">+5.2% vs anterior</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* KPIs Grid */}
      {selectedWidgets.kpis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {Object.entries(dashboardData.kpis).map(([key, value]) => {
            const Icon = getKpiIcon(key);
            const isPercentage = typeof value === 'number' && value <= 100;
            const isTime = key === 'timeToMarket';
            
            return (
              <div key={key} className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                  <Icon className="h-8 w-8 text-blue-600" />
                  <span className={`text-2xl font-bold ${getKpiColor(value, isPercentage ? 'percentage' : isTime ? 'time' : 'default')}`}>
                    {isTime ? `${value} días` : isPercentage ? `${value}%` : value}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${isPercentage ? value : Math.min(100, (value / 100) * 100)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </motion.div>
      )}

      {/* Charts and Trends */}
      {selectedWidgets.trends && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Trends Chart */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendencias de Crecimiento</h3>
            <div className="h-64 flex items-end justify-between space-x-2">
              {dashboardData.trends.producersGrowth.map((item, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="bg-blue-600 rounded-t w-full transition-all duration-300 hover:bg-blue-700"
                    style={{ height: `${(item.value / 100) * 200}px` }}
                    title={`${item.month}: ${item.value}`}
                  ></div>
                  <span className="text-xs text-gray-600 mt-2">{item.month}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Productivity Chart */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Productividad por Área</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Maíz</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  <span className="text-sm font-medium">85%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tomate</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '72%' }}></div>
                  </div>
                  <span className="text-sm font-medium">72%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Papa</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '68%' }}></div>
                  </div>
                  <span className="text-sm font-medium">68%</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts */}
        {selectedWidgets.alerts && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              Alertas Recientes
            </h3>
            <div className="space-y-3">
              {dashboardData.alerts.map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    alert.type === 'warning' ? 'bg-yellow-500' :
                    alert.type === 'error' ? 'bg-red-500' :
                    alert.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{alert.message}</p>
                    <p className="text-xs text-gray-500">{alert.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Recent Activities */}
        {selectedWidgets.activities && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 text-blue-600 mr-2" />
              Actividades Recientes
            </h3>
            <div className="space-y-3">
              {dashboardData.recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">por {activity.user} • {activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Weather Widget */}
        {selectedWidgets.weather && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Sun className="h-5 w-5 text-yellow-600 mr-2" />
              Condiciones Climáticas
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Temperatura</span>
                <span className="text-lg font-semibold">{dashboardData.weatherData.temperature}°C</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Humedad</span>
                <span className="text-lg font-semibold">{dashboardData.weatherData.humidity}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Precipitación</span>
                <span className="text-lg font-semibold">{dashboardData.weatherData.rainfall}mm</span>
              </div>
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Pronóstico</h4>
                <div className="space-y-2">
                  {dashboardData.weatherData.forecast.map((day, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{day.day}</span>
                      <div className="flex items-center space-x-2">
                        <span>{day.temp}°C</span>
                        <span className="text-gray-500">{day.condition}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
